import { useState } from "react";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus, Trash2, Webhook, Pencil, Eye, Send, CheckCircle, XCircle, Clock, Copy, RefreshCw, Loader2} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { PageHeader } from "@/components/PageHeader";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";
import { formatDistanceToNow } from "date-fns";

interface WebhookSub {
  id: string;
  url: string;
  description: string | null;
  eventTypes: string[];
  secretKey: string;
  isActive: boolean;
}
interface WebhookLog {
  id: string;
  eventType: string;
  responseStatus: number | null;
  responseTimeMs: number | null;
  retryCount: number;
  deliveryStatus: string;
  error: string | null;
  createdAt: string;
}

const EVENT_TYPE_LABELS: Record<string, string> = {
  "order.created": "Order Created",
  "order.status_changed": "Order Status Changed",
  "invoice.created": "Invoice Created",
  "quote.created": "Sales Quote Created",
  "account.created": "Account Created",
  "account.updated": "Account Updated",
};

export default function WebhooksPage() {
  const { sessionId } = useAuth();

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ url: "", description: "", eventTypes: [] as string[], isActive: true });
  const [secretVisible, setSecretVisible] = useState<string | null>(null);
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [viewingSubId, setViewingSubId] = useState<string | null>(null);

  const { data: subs = [], isLoading } = useQuery<WebhookSub[]>({
    queryKey: ["/api/webhooks"],
    queryFn: () => api.get<WebhookSub[]>(`/webhooks`, sessionId),
  });

  const { data: logsData } = useQuery<{ logs: WebhookLog[]; total: number }>({
    queryKey: ["/api/webhooks", viewingSubId, "logs"],
    queryFn: () => api.get<{ logs: WebhookLog[]; total: number }>(`/webhooks/${viewingSubId}/logs?limit=50`, sessionId),
    enabled: !!viewingSubId && logsDialogOpen,
  });

  const saveMutation = useMutation({
    mutationFn: () => editingId
      ? api.patch(`/webhooks/${editingId}`, form, sessionId)
      : api.post(`/webhooks`, form, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/webhooks"] });
      setDialogOpen(false);
      setEditingId(null);
      toast({ title: editingId ? "Webhook updated" : "Webhook created" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/webhooks/${id}`, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/webhooks"] });
      toast({ title: "Webhook deleted" });
    },
  });

  const testMutation = useMutation({
    mutationFn: (id: string) =>
      api.post<{ success: boolean; statusCode: number | null; responseTimeMs: number; error: string | null }>(`/webhooks/${id}/test`, undefined, sessionId),
    onSuccess: (result) => {
      if (result.success) {
        toast({ title: "Test delivered", description: `Status ${result.statusCode} in ${result.responseTimeMs}ms` });
      } else {
        toast({ title: "Test failed", description: result.error || `Status ${result.statusCode}`, variant: "destructive" });
      }
      queryClient.invalidateQueries({ queryKey: ["/api/webhooks"] });
    },
    onError: () => toast({ title: "Test failed", variant: "destructive" }),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch(`/webhooks/${id}`, { isActive }, sessionId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/webhooks"] }),
  });

  const handleNew = () => {
    setEditingId(null);
    setForm({ url: "", description: "", eventTypes: [], isActive: true });
    setDialogOpen(true);
  };

  const handleEdit = (sub: WebhookSub) => {
    setEditingId(sub.id);
    setForm({ url: sub.url, description: sub.description || "", eventTypes: [...sub.eventTypes], isActive: sub.isActive });
    setDialogOpen(true);
  };

  const toggleEventType = (et: string) => setForm((f) => ({
    ...f, eventTypes: f.eventTypes.includes(et) ? f.eventTypes.filter((e) => e !== et) : [...f.eventTypes, et],
  }));

  const statusIcon = (status: string) => {
    switch (status) {
      case "delivered": return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "failed": return <XCircle className="h-4 w-4 text-red-500" />;
      case "retrying": return <RefreshCw className="h-4 w-4 text-orange-500 animate-spin" />;
      default: return <Clock className="h-4 w-4 text-yellow-500" />;
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Webhooks"
        description="Configure outbound webhooks to notify external systems when events occur."
        actions={
          <div className="flex items-center gap-2">
            <Button onClick={handleNew}>
              <Plus className="h-4 w-4 mr-1" /> Add Webhook
            </Button>
          </div>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Webhook className="h-4 w-4" /> Webhook Subscriptions ({subs.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {subs.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No webhooks configured. Add a webhook subscription to start receiving event notifications.
            </p>
          ) : (
            <div className="space-y-3">
              {subs.map((sub) => (
                <div key={sub.id} className="p-3 rounded-md border">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm font-mono truncate max-w-[400px]">{sub.url}</span>
                        {!sub.isActive && <Badge variant="outline" className="text-[10px]">Disabled</Badge>}
                      </div>
                      {sub.description && <p className="text-xs text-muted-foreground mt-0.5">{sub.description}</p>}
                      <div className="flex flex-wrap gap-1 mt-2">
                        {sub.eventTypes.map((et) => (
                          <Badge key={et} variant="secondary" className="text-[10px]">
                            {EVENT_TYPE_LABELS[et] || et}
                          </Badge>
                        ))}
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-muted-foreground">Secret:</span>
                        <code className="text-xs bg-muted px-2 py-0.5 rounded flex-1 truncate max-w-[300px]">
                          {secretVisible === sub.id ? sub.secretKey : "•".repeat(32)}
                        </code>
                        <Button
                          variant="ghost" size="icon" className="h-6 w-6"
                          onClick={() => setSecretVisible(secretVisible === sub.id ? null : sub.id)}
                        >
                          <Eye className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost" size="icon" className="h-6 w-6"
                          onClick={() => {
                            navigator.clipboard.writeText(sub.secretKey);
                            toast({ title: "Secret copied" });
                          }}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <label className="inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={sub.isActive}
                          onChange={(e) => toggleMutation.mutate({ id: sub.id, isActive: e.target.checked })}
                        />
                        <div className="w-9 h-5 bg-muted peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-all after:mt-0.5 after:ml-0.5 relative peer-checked:bg-primary" />
                      </label>
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8"
                        title="Send test event"
                        onClick={() => testMutation.mutate(sub.id)}
                      >
                        <Send className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8"
                        title="View delivery logs"
                        onClick={() => { setViewingSubId(sub.id); setLogsDialogOpen(true); }}
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(sub)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8"
                        onClick={() => { if (confirm("Delete this webhook?")) deleteMutation.mutate(sub.id); }}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Webhook" : "Add Webhook"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Endpoint URL</label>
              <Input
                placeholder="https://example.com/webhook"
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Description (optional)</label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Event Types</label>
              <div className="space-y-2">
                {Object.entries(EVENT_TYPE_LABELS).map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={form.eventTypes.includes(key)}
                      onCheckedChange={() => toggleEventType(key)}
                    />
                    {label}
                    <code className="text-xs text-muted-foreground ml-auto">{key}</code>
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
              Active
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() => saveMutation.mutate()}
              disabled={!form.url || form.eventTypes.length === 0 || saveMutation.isPending}
            >
              {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingId ? "Save Changes" : "Create Webhook"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={logsDialogOpen} onOpenChange={setLogsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Delivery Logs</DialogTitle>
          </DialogHeader>
          {!logsData?.logs.length ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No delivery logs yet.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>HTTP</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead>Retries</TableHead>
                  <TableHead>Error</TableHead>
                  <TableHead>When</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logsData.logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>{statusIcon(log.deliveryStatus)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {EVENT_TYPE_LABELS[log.eventType] || log.eventType}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {log.responseStatus ? (
                        <span className={log.responseStatus >= 200 && log.responseStatus < 300 ? "text-green-600" : "text-red-600"}>
                          {log.responseStatus}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>{log.responseTimeMs != null ? `${log.responseTimeMs}ms` : "—"}</TableCell>
                    <TableCell>{log.retryCount}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate" title={log.error || undefined}>
                      {log.error || "—"}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(log.createdAt), { addSuffix: true })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
